/* The Clients list's Open column, in one query.

   Three hooks now read `interactions`, and they are deliberately different
   shapes:

     useInteractions.js    the handful of records for ONE open drawer, live.
     useAllInteractions.js the whole collection, once, for the ⌘K palette.
     this one              every unresolved open item, for a whole list column.

   The Open column has to answer "is this account waiting on something?" for
   every row on screen, and the two things it must not do are load the
   collection (it holds email bodies) or fire a query per row. So it asks the
   one question it actually has: type == "open_item" AND status == "open".
   Two equality clauses, no orderBy, so no composite index is needed and the
   result carries summaries only — an open_item record has no body field at
   all (tools/crm/interactlib.mjs).

   Admin-only, like the other two. The Firestore rule on `interactions` is the
   actual boundary; the role check here is a courtesy that stops a crew session
   firing a request that is going to come back permission-denied. */
import { useEffect, useState } from "react";
import { db, collection, getDocs, query } from "../../firebase.js";
import { where } from "firebase/firestore";
import { makeSessionCache, RETRY_AFTER_FAILURE_MS } from "./sessionCache.js";

const COLLECTION = "interactions";
const ADMIN_ROLES = new Set(["admin", "superadmin"]);

export { RETRY_AFTER_FAILURE_MS };

async function fetchOpenItems() {
  const q = query(
    collection(db, COLLECTION),
    where("type", "==", "open_item"),
    where("status", "==", "open"),
  );
  const snap = await getDocs(q);
  // The document id wins over any `id` field inside the document, the same
  // rule the other two hooks follow.
  return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
}

/* One in-flight or settled read per session, with the same failure backoff the
   palette's loader uses. The column is not live: an open item resolved in
   another tab shows up on the next reload, which is the right trade for a
   query that would otherwise run on every list render. */
const store = makeSessionCache(fetchOpenItems, { label: "[useOpenItems]" });

/** Test seam — the ?e2e=crm fixture has no Firebase and seeds this directly.
 *  Passing null clears it. Not used by app code. */
export function __setOpenItemsCache(records) {
  if (records === null) store.clear();
  else store.seed(records);
}

/** Drop the session copy. Called from the sign-out path in src/App.jsx. */
export function __clearCrmCaches() {
  store.clear();
}

/** Test seam: is anything cached right now? */
export function __hasCache() {
  return store.has();
}

/**
 * @param role normalized role from jobAccess()
 * @returns {{ records, loaded, allowed }} — records is [] until the read
 *   settles, and stays [] for a role that may not ask or a read that failed.
 */
export function useOpenItems(role) {
  const allowed = ADMIN_ROLES.has(role);
  const [records, setRecords] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Losing the role drops what this session already read, rather than
    // leaving another account's open items on screen.
    if (!allowed) {
      store.clear();   // the shared copy goes with the right to read it
      setRecords([]);
      setLoaded(false);
      return undefined;
    }
    let alive = true;
    const pending = store.load();
    if (!pending) return undefined;   // a recent load failed; wait out the backoff
    pending.then(({ records: rs }) => {
      if (!alive) return;
      setRecords(rs);
      setLoaded(true);
    });
    return () => { alive = false; };
  }, [allowed]);

  return { records, loaded, allowed };
}

export default useOpenItems;
